import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useInvoiceReceipts, useReverseInvoiceReceipt } from "@/hooks/useInvoices";
import { usePermissions } from "@/hooks/usePermissions";
import type { PaymentReceipt, ReverseLotAction } from "@/interfaces/invoices";
import { RECEIPT_KIND, RECEIPT_METHOD_LABEL } from "@/interfaces/invoices";

const money = (v: string | number | null | undefined, ccy = "usd") =>
  v == null
    ? "—"
    : `${ccy.toUpperCase()} ${Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
const dateTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** A receipt's badge: Reversal (if it negates another), else Payment / Write-off. */
function kindBadge(r: PaymentReceipt): { label: string; color: "default" | "success" | "warning" } {
  if (r.reversesReceiptId != null) return { label: "Reversal", color: "default" };
  if (r.kind === RECEIPT_KIND.write_off) return { label: "Write-off", color: "warning" };
  return { label: "Payment", color: "success" };
}

/** The invoice's payment-receipts ledger, with an append-only Reverse action (FINANCE-gated). */
export default function InvoiceReceipts({
  groupId,
  invoiceId,
  currency,
  isMinted = false,
}: {
  groupId: number;
  invoiceId: number;
  currency: string;
  /** Whether this invoice has minted credits — gates the hold/reverse options on the reverse prompt. */
  isMinted?: boolean;
}) {
  const { can } = usePermissions();
  const { data: receipts = [], isLoading } = useInvoiceReceipts(groupId, invoiceId);
  const reverse = useReverseInvoiceReceipt(groupId, invoiceId);
  const [target, setTarget] = useState<PaymentReceipt | null>(null);
  const [reason, setReason] = useState("");
  const [lotAction, setLotAction] = useState<ReverseLotAction>("none");
  const [error, setError] = useState<string | null>(null);

  const closeDialog = () => {
    setTarget(null);
    setReason("");
    setLotAction("none");
    setError(null);
  };
  const submitReverse = () => {
    if (!target) return;
    setError(null);
    reverse.mutate(
      { receiptId: target.id, reason: reason.trim() || null, lotAction },
      {
        onSuccess: closeDialog,
        onError: (e) => setError(e instanceof Error ? e.message : "Couldn't reverse the receipt."),
      },
    );
  };

  const canReverse = can("cancel", "FINANCE");
  // A receipt is already reversed if some row negates it.
  const reversedIds = useMemo(
    () => new Set(receipts.map((r) => r.reversesReceiptId).filter((x): x is number => x != null)),
    [receipts],
  );

  if (isLoading) {
    return <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>Loading…</Typography>;
  }
  if (receipts.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
        No payments yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {receipts.map((r) => {
        const badge = kindBadge(r);
        const isReversal = r.reversesReceiptId != null;
        const alreadyReversed = reversedIds.has(r.id);
        const reversible = canReverse && !isReversal && !alreadyReversed;
        return (
          <Box
            key={r.id}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              border: "1px solid var(--color-smoke)",
              borderRadius: "8px",
              p: 1.5,
              opacity: alreadyReversed ? 0.6 : 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  size="small"
                  label={badge.label}
                  color={badge.color}
                  variant="outlined"
                  sx={{ height: 20 }}
                />
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: Number(r.amount) < 0 ? "var(--color-red, #c00)" : "inherit",
                  }}
                >
                  {money(r.amount, currency)}
                </Typography>
                {r.method != null && (
                  <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                    · {RECEIPT_METHOD_LABEL[r.method] ?? ""}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                {dateTime(r.receivedAt)} · {r.actorName ?? "System"}
                {r.reference ? ` · ${r.reference}` : ""}
              </Typography>
              {r.note && (
                <Typography sx={{ fontSize: 12.5, color: "var(--color-rodeo-black)" }}>
                  {r.note}
                </Typography>
              )}
            </Box>

            {reversible && (
              <Button
                size="small"
                color="error"
                sx={{ flexShrink: 0 }}
                onClick={() => setTarget(r)}
              >
                Reverse
              </Button>
            )}
            {alreadyReversed && (
              <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", flexShrink: 0 }}>
                Reversed
              </Typography>
            )}
          </Box>
        );
      })}

      {target && (
        <ReverseDialog
          receipt={target}
          currency={currency}
          reason={reason}
          onReason={setReason}
          lotAction={lotAction}
          onLotAction={setLotAction}
          // Credits only exist to hold/reverse when the target is a real payment on a minted invoice.
          showLotOptions={isMinted && target.kind !== RECEIPT_KIND.write_off}
          onConfirm={submitReverse}
          onClose={closeDialog}
          isPending={reverse.isPending}
          error={error}
        />
      )}
    </Box>
  );
}

/**
 * Confirmation for reversing a receipt. Reversal is LEDGER-ONLY — it never touches Stripe (Stripe
 * doesn't allow reversing a recorded payment), so the dialog says so plainly and spells out what the
 * reversal does before the user commits.
 */
function ReverseDialog({
  receipt,
  currency,
  reason,
  onReason,
  lotAction,
  onLotAction,
  showLotOptions,
  onConfirm,
  onClose,
  isPending,
  error,
}: {
  receipt: PaymentReceipt;
  currency: string;
  reason: string;
  onReason: (v: string) => void;
  lotAction: ReverseLotAction;
  onLotAction: (v: ReverseLotAction) => void;
  showLotOptions: boolean;
  onConfirm: () => void;
  onClose: () => void;
  isPending: boolean;
  error: string | null;
}) {
  const kindLabel = receipt.kind === RECEIPT_KIND.write_off ? "write-off" : "payment";
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        Reverse {kindLabel} of {money(receipt.amount, currency)}?
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}
      >
        {/* The one thing finance must understand before proceeding. */}
        <Box
          sx={{
            border: "1px solid var(--color-smoke)",
            bgcolor: "var(--color-cloud, #f7f5f1)",
            borderRadius: "8px",
            p: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, mb: 0.5 }}>
            This reverses the GiftForward ledger only — not Stripe.
          </Typography>
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            Stripe doesn't allow reversing a recorded payment, so the Stripe invoice stays exactly
            as it is. Reconcile the Stripe side manually if needed.
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>What this will do</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, color: "var(--color-graphite)", fontSize: 13 }}>
            <li>Add an append-only reversal entry (the original stays in the log).</li>
            <li>
              Recompute this invoice's paid amount and status (it may return to Pending/Partial).
            </li>
          </Box>
        </Box>

        {/* Credit-lot decision — only when there are minted credits to act on. Default: leave them. */}
        {showLotOptions && (
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              What happens to the minted credits?
            </Typography>
            <RadioGroup
              value={lotAction}
              onChange={(e) => onLotAction(e.target.value as ReverseLotAction)}
            >
              <FormControlLabel
                value="none"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: 13.5 }}>
                    Do nothing — I'll handle the credits manually
                  </Typography>
                }
              />
              <FormControlLabel
                value="hold"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: 13.5 }}>
                    Put the credits on hold — pause them pending resolution
                  </Typography>
                }
              />
              <FormControlLabel
                value="reverse"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: 13.5 }}>
                    Reverse the credits — full clawback; any already-spent credits are written off
                    to goodwill
                  </Typography>
                }
              />
            </RadioGroup>
          </Box>
        )}

        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => onReason(e.target.value)}
          size="small"
          multiline
          minRows={2}
          placeholder="e.g. Recorded against the wrong invoice"
          helperText="Recorded on the reversal entry for the audit trail."
        />
        {error && (
          <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 13 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={isPending}>
          {isPending ? "Reversing…" : "Reverse (ledger only)"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
